import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HelpCenter, HelpTip, NewVersionBanner, ToastProvider } from '@dataroom/shared';
import { Layout, PortalFrame } from '@/components/Layout';
import { ChecklistPage } from '@/pages/ChecklistPage';
import { contractsEnabled } from '@/lib/features';
import { ContractsPage } from '@/pages/ContractsPage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { ProcessingPage } from '@/pages/ProcessingPage';
import { LoginPage } from '@/pages/LoginPage';
import { LoisPage } from '@/pages/LoisPage';
import { QuestionsPage } from '@/pages/QuestionsPage';
import { RisksPage } from '@/pages/RisksPage';
import { RoomsLandingPage } from '@/pages/RoomsLandingPage';
import { SubmitPage } from '@/pages/SubmitPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

// The hosted mock demo is served as static files (no server-side SPA
// fallback), so it uses hash routing to survive deep-link refreshes. The real
// app uses history-based routing behind the Pages/API origin.
const Router = import.meta.env.VITE_USE_MOCK === '1' ? HashRouter : BrowserRouter;

/** Offers a reload once a newer build is deployed (web/shared/src/ui/new-version-banner.tsx).
 * Off in dev and in the mock demo build, where there is no deploy to compare. */
function NewVersionNotice() {
  const { pathname } = useLocation();
  return (
    <NewVersionBanner
      currentVersion={import.meta.env.VITE_APP_VERSION}
      enabled={import.meta.env.PROD && import.meta.env.VITE_USE_MOCK !== '1'}
      routeKey={pathname}
      messageAside={<HelpTip id="seller.app.new-version-bar" />}
    />
  );
}

/** The route table, exported so tests can mount it under a MemoryRouter. */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RoomsLandingPage />} />
      {/* The help center: static content, so it needs no room and works signed in or out. */}
      <Route
        path="/help/*"
        element={
          <PortalFrame>
            <HelpCenter audience="seller" />
          </PortalFrame>
        }
      />
      <Route path="/rooms/:roomId" element={<Layout />}>
        <Route index element={<SubmitPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="processing" element={<ProcessingPage />} />
        <Route path="checklist" element={<ChecklistPage />} />
        <Route path="lois" element={<LoisPage />} />
        <Route path="questions" element={<QuestionsPage />} />
        <Route path="risks" element={<RisksPage />} />
        {/* Off on PROD (VITE_FEATURE_CONTRACTS=0): a deep link lands on the room home. */}
        <Route
          path="contracts"
          element={
            contractsEnabled() ? <ContractsPage /> : <Navigate to=".." relative="route" replace />
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Confirms what a bulk action did ("3 merged") — the possible-new-versions list. */}
      <ToastProvider>
        <Router>
          <AppRoutes />
          <NewVersionNotice />
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}
